<template>
  <section aria-label="Video player" class="h-full w-full">
    <div class="rv-video__outer">
      <youtube
        v-if="!youTubeIsBlocked"
        ref="youtube"
        :video-id="youTubeId(video.src)"
        :player-vars="playerVars"
        :width="'100%'"
        :height="'100%'"
        class="rv-video__inner"
        @playing="handlePlay"
        @ended="handleStop"
        @paused="handleStop"
      />
      <div v-else-if="internationalFallback" class="rv-video__inner">
        <iframe
          :src="internationalFallback"
          width="100%"
          height="100%"
          frameborder="0"
          allowfullscreen
        />
      </div>
    </div>

    <ul
      v-if="post.transcription || post.video.audio_described"
      class="lg:flex lg:justify-between mb-2 p-x lg:px-1"
    >
      <li v-if="post.transcription">
        <button
          aria-controls="video-transcription"
          data-gtm-transcription="transcription"
          :aria-expanded="transcription"
          class="mt-2 focus-a underline"
          :class="[textcolor]"
          @click="transcription = !transcription"
        >
          {{ transcription ? 'Hide transcription' : 'Show transcription' }}
        </button>
      </li>
      <li v-if="post.video.audio_described && !youTubeIsBlocked">
        <button
          class="mt-2 focus-a underline"
          :class="[textcolor]"
          data-gtm-audio-described="ytIsAudioDescribed"
          @click="$store.commit('toggleAudioDescription')"
        >
          {{ video.text }}
        </button>
      </li>
    </ul>
    <Expand v-show="transcription">
      <div
        id="video-transcription"
        class="tiny-mce mb-6 p-x lg:px-0 lg:mx-0 py-4 overflow-auto"
        :class="[textcolor]"
        v-html="post.transcription"
      />
    </Expand>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import Expand from '@/components/utility/Expand.vue'

export default {
  name: 'Video',

  components: {
    Expand
  },

  props: {
    post: {
      type: Object,
      default: () => {}
    },
    textcolor: {
      type: String,
      default: 'text-white'
    }
  },

  data: () => ({
    playerVars: {
      rel: 0,
      modestbranding: 1,
      origin: window.location.hostname
    },
    transcription: false,
    youTubeIsBlocked: false
  }),

  computed: {
    ...mapState(['ytIsAudioDescribed', 'browser']),

    player() {
      return this.$refs.youtube.player
    },

    video() {
      const src =
        this.post.video.audio_described && this.ytIsAudioDescribed
          ? this.post.video.audio_described
          : this.post.video.non_described
      const text =
        this.post.video.audio_described && this.ytIsAudioDescribed
          ? 'Watch non audio described version'
          : this.post.video.audio_described
          ? 'Watch audio described version'
          : ''

      return {
        src: src,
        text: text
      }
    },

    internationalFallback() {
      // Not really sure what they will post in here but assuming some kind of iframe or src
      const string = this.post.video.international_fallback
      // Extract anything resembling a url from the string
      const urlMaybe = string.match(/(https?:\/\/[^ ]*)/)
      // If there is a result the above regex doesnt remove a trailing "
      // eg: if extracted from a src="", hence the replace regex on the result
      const src = urlMaybe && urlMaybe[0].replace(/"$/, '')
      return src
    }
  },

  async created() {
    /*
     * Check if YouTube is available
     * If getting a CORS header, we're hitting Youtube's servers,
     * which likely means that youtube is not "blocked"
     * NB this doesn't seem to work for ie11.
     */
    try {
      await fetch('https://www.youtube.com/', {
        mode: 'no-cors'
      })
      return (this.youTubeIsBlocked = false)
    } catch {
      if (this.browser !== 'IE') {
        return (this.youTubeIsBlocked = true)
      }
      return (this.youTubeIsBlocked = false)
    }
  },

  methods: {
    playVideo() {
      this.player.playVideo()
    },
    stopVideo() {
      this.player.stopVideo()
    },
    handlePlay() {
      this.$store.commit('toggleYouTubeState', true)
    },
    handleStop() {
      this.$store.commit('toggleYouTubeState', false)
    },
    youTubeId(inputUrl) {
      const outputUrl = inputUrl.split(
        /(vi\/|v%3D|v=|\/v\/|youtu\.be\/|\/embed\/)/
      )
      return undefined !== outputUrl[2]
        ? outputUrl[2].split(/[^0-9a-z_\-]/i)[0]
        : outputUrl[0]
    }
  }
}
</script>

<style>
.rv-video__outer {
  height: 0;
  padding-top: 56.25%;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.rv-video__inner {
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}
</style>
